import Keycloak from 'keycloak-js';
import type { User } from '@types/index';

/**
 * Authentication service using Keycloak
 */
class AuthService {
  private keycloak: Keycloak | null = null;
  private initialized = false;
  private initializing = false;
  private initPromise: Promise<boolean> | null = null;

  /**
   * Initialize Keycloak instance
   */
  async init(): Promise<boolean> {
    // If already initialized, return current auth status
    if (this.initialized && this.keycloak) {
      return this.keycloak?.authenticated || false;
    }

    // If currently initializing, wait for the existing initialization to complete
    if (this.initializing && this.initPromise) {
      return this.initPromise;
    }

    // Mark as initializing to prevent concurrent init calls
    this.initializing = true;

    // Store the initialization promise so concurrent calls can wait
    this.initPromise = (async () => {
      try {
        return await this.performInit();
      } finally {
        this.initializing = false;
        this.initPromise = null;
      }
    })();

    return this.initPromise;
  }

  /**
   * Perform the actual Keycloak initialization
   */
  private async performInit(): Promise<boolean> {
    try {
      // Check for stored tokens
      const storedToken = localStorage.getItem('kc_token');
      const storedRefreshToken = localStorage.getItem('kc_refreshToken');

      // Only create a new Keycloak instance if one doesn't exist
      if (!this.keycloak) {
        this.keycloak = new Keycloak({
          url: import.meta.env.VITE_KEYCLOAK_URL,
          realm: import.meta.env.VITE_KEYCLOAK_REALM,
          clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID,
        });

        // Try to restore token from localStorage if available
        if (storedToken && storedRefreshToken) {
          this.keycloak.token = storedToken;
          this.keycloak.refreshToken = storedRefreshToken;
          const storedIdToken = localStorage.getItem('kc_idToken');
          if (storedIdToken) {
            this.keycloak.idToken = storedIdToken;
          }
        }
      } else {
        // If instance exists and already initialized, return current state
        if (this.initialized) {
          return this.keycloak.authenticated || false;
        }
      }

      // Initialize with PKCE for Authorization Code Flow
      const initOptions: any = {
        pkceMethod: 'S256',
        checkLoginIframe: true,
        enableLogging: false,
        onLoad: 'check-sso',
        silentCheckSsoRedirectUri: window.location.origin + '/silent-check-sso.html',
        responseMode: 'query',
        flow: 'standard',
      };

      // If we have stored tokens, provide them to Keycloak init
      if (storedToken && storedRefreshToken) {
        initOptions.token = storedToken;
        initOptions.refreshToken = storedRefreshToken;
        const storedIdToken = localStorage.getItem('kc_idToken');
        if (storedIdToken) {
          initOptions.idToken = storedIdToken;
        }
      }

      const authenticated = await this.keycloak.init(initOptions).catch((error) => {
        console.error('Keycloak initialization failed:', error);

        // If init fails but we have stored tokens, clear them
        if (storedToken) {
          localStorage.removeItem('kc_token');
          localStorage.removeItem('kc_refreshToken');
          localStorage.removeItem('kc_idToken');
        }

        throw error;
      });

      this.initialized = true;

      // Store tokens in localStorage for persistence across page refreshes
      if (authenticated && this.keycloak.token && this.keycloak.refreshToken) {
        localStorage.setItem('kc_token', this.keycloak.token);
        localStorage.setItem('kc_refreshToken', this.keycloak.refreshToken);
        if (this.keycloak.idToken) {
          localStorage.setItem('kc_idToken', this.keycloak.idToken);
        }
      }

      // Setup event handlers
      this.keycloak.onAuthSuccess = () => {
        // Persist tokens on auth success
        if (this.keycloak?.token && this.keycloak?.refreshToken) {
          localStorage.setItem('kc_token', this.keycloak.token);
          localStorage.setItem('kc_refreshToken', this.keycloak.refreshToken);
          if (this.keycloak.idToken) {
            localStorage.setItem('kc_idToken', this.keycloak.idToken);
          }
        }
      };

      this.keycloak.onAuthError = (error) => {
        console.error('Authentication error:', error);
      };

      this.keycloak.onAuthRefreshSuccess = () => {
        // Update stored tokens after refresh
        if (this.keycloak?.token && this.keycloak?.refreshToken) {
          localStorage.setItem('kc_token', this.keycloak.token);
          localStorage.setItem('kc_refreshToken', this.keycloak.refreshToken);
          if (this.keycloak.idToken) {
            localStorage.setItem('kc_idToken', this.keycloak.idToken);
          }
        }
      };

      this.keycloak.onAuthRefreshError = () => {
        console.error('Token refresh failed');
        // Clear invalid tokens
        localStorage.removeItem('kc_token');
        localStorage.removeItem('kc_refreshToken');
        localStorage.removeItem('kc_idToken');
      };

      this.keycloak.onAuthLogout = () => {
        // Clear tokens on logout
        localStorage.removeItem('kc_token');
        localStorage.removeItem('kc_refreshToken');
        localStorage.removeItem('kc_idToken');
      };

      this.keycloak.onTokenExpired = () => {
        this.keycloak?.updateToken(30).catch(() => {
          console.error('Failed to refresh expired token');
          localStorage.removeItem('kc_token');
          localStorage.removeItem('kc_refreshToken');
          localStorage.removeItem('kc_idToken');
        });
      };

      this.keycloak.onReady = (authenticated) => {
        // Persist tokens when ready
        if (authenticated && this.keycloak?.token && this.keycloak?.refreshToken) {
          localStorage.setItem('kc_token', this.keycloak.token);
          localStorage.setItem('kc_refreshToken', this.keycloak.refreshToken);
          if (this.keycloak.idToken) {
            localStorage.setItem('kc_idToken', this.keycloak.idToken);
          }
        }
      };

      // Setup token refresh
      if (authenticated) {
        this.setupTokenRefresh();
      }

      return authenticated;
    } catch (error) {
      console.error('Failed to initialize Keycloak:', error);
      this.initialized = true; // Mark as initialized even on error
      return false;
    }
  }

  /**
   * Setup automatic token refresh
   */
  private setupTokenRefresh(): void {
    if (!this.keycloak) return;

    // Refresh token 60 seconds before expiry
    setInterval(() => {
      this.keycloak
        ?.updateToken(60)
        .then((refreshed) => {
          // Token refreshed successfully
        })
        .catch(() => {
          console.error('Failed to refresh token');
          this.logout();
        });
    }, 60000); // Check every minute
  }

  /**
   * Initialize Keycloak specifically for handling OAuth callback
   */
  async initializeCallback(): Promise<boolean> {
    // If already initializing or initialized, return the current state
    if (this.initializing) {
      await new Promise(resolve => setTimeout(resolve, 100));
      return this.keycloak?.authenticated || false;
    }

    if (this.initialized && this.keycloak) {
      return this.keycloak.authenticated || false;
    }

    // Reset and call regular init to process the auth code
    this.initialized = false;
    this.initializing = false;

    return this.init();
  }

  /**
   * Login with Keycloak
   */
  async login(): Promise<void> {
    if (!this.keycloak) {
      throw new Error('Keycloak not initialized');
    }

    const loginOptions = {
      redirectUri: window.location.origin + '/auth/callback'
    };

    await this.keycloak.login(loginOptions);
  }

  /**
   * Logout from Keycloak
   */
  async logout(): Promise<void> {
    if (!this.keycloak) {
      throw new Error('Keycloak not initialized');
    }

    // Clear tokens from localStorage
    localStorage.removeItem('kc_token');
    localStorage.removeItem('kc_refreshToken');
    localStorage.removeItem('kc_idToken');

    const logoutOptions = {
      redirectUri: window.location.origin + '/login'
    };

    await this.keycloak.logout(logoutOptions);
  }

  /**
   * Get current user information
   */
  getUser(): User | null {
    if (!this.keycloak?.idTokenParsed) {
      return null;
    }

    const idToken = this.keycloak.idTokenParsed as any;
    const accessToken = this.keycloak.tokenParsed as any;

    return {
      id: idToken.sub || '',
      email: idToken.email || '',
      name: idToken.name || idToken.preferred_username || `${idToken.given_name || ''} ${idToken.family_name || ''}`.trim(),
      role: accessToken?.realm_access?.roles?.[0] || 'user',
      avatar: idToken.picture,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
  }

  /**
   * Get authentication token
   */
  getToken(): string | undefined {
    return this.keycloak?.token;
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return this.keycloak?.authenticated || false;
  }

  /**
   * Check if user has specific role
   */
  hasRole(role: string): boolean {
    return this.keycloak?.hasRealmRole(role) || false;
  }

  /**
   * Get Keycloak instance
   */
  getKeycloakInstance(): Keycloak | null {
    return this.keycloak;
  }
}

// Export singleton instance
export const authService = new AuthService();
